import { useCallback, useEffect, useState } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

import { readComposeParams, stripComposeParamsFromUrl } from './composeUrl';
import mayStartConversations from './mayStartConversations';

// Sentinel id for the ephemeral (not-yet-persisted) conversation shown in the right pane while
// composing. It never hits the API — the real UUID arrives only after the first message creates
// the conversation. Kept stable so child components (Messages/useAutoScroll) key consistently.
export const DRAFT_CONVERSATION_ID = '__compose_draft__';

// Which mailbox a new thread lands in. Promo-entity origin types (Expert/Product/Company) never
// match the promoter sub-branches, so in practice this is "focused" for media senders and "other"
// for promoters — the full shape covers other messagedProfile types.
function composeMailbox(messagedProfileType) {
  const senderType = globalThis.Qwoted.CURRENT_PROFILE_TYPE || '';
  if (senderType === 'type_media') return 'focused';
  if (senderType.includes('promoter')) {
    if (messagedProfileType === 'type_media') return 'focused';
    if (typeof messagedProfileType === 'string' && messagedProfileType.includes('promoter')) return 'focused';
  }
  return 'other';
}

// Normalize the representations rows into the recipient list + the entity being messaged about,
// shaped like a conversations-view row so the inbox header/composer render against it unchanged.
function buildDraftFromRepresentations(rows, { originType, originId, subject: subjectOverride }) {
  const isCompany = originType === 'Id::PromoEntity::Company';

  const reps = rows
    .map((r) => ({
      slug: r.promoter_profile_slug,
      name: r.promoter_profile_name,
      profile_image_url: r.promoter_profile_image_url,
      title: r.promoter_job_title,
      type_of_profile: 'Id::Profile',
    }))
    .filter((profile, index, array) => array.findIndex((p) => p.slug === profile.slug) === index);

  const first = rows[0] || {};
  const entity = {
    name: isCompany ? first.promoted_company_name : first.promoted_entity_name,
    slug: isCompany ? first.promoted_company_slug : first.promoted_entity_slug,
    profile_image_url: isCompany ? first.promoted_company_profile_image_url : first.promoted_entity_profile_image_url,
    title: originType === 'Id::PromoEntity::Expert' ? first.promoted_expert_job_title : '',
  };

  // A caller (e.g. "Request Sample") may override the auto-derived subject with a marker subject.
  const subject = (subjectOverride || entity.name || 'New message').slice(0, 200);

  return {
    conversation_id: DRAFT_CONVERSATION_ID,
    is_draft: true,
    subject,
    origin_type: originType,
    origin_id: originId,
    // The header headlines the entity via origin_expert/origin_product (Company has no dedicated
    // slot, so it falls back to headlining the reps — acceptable for the compose preview).
    origin_expert: originType === 'Id::PromoEntity::Expert' ? entity : null,
    origin_product: originType === 'Id::PromoEntity::Product' ? entity : null,
    conversation_receivers: reps,
    conversation_senders: [],
    last_sent_at: null,
    // Everything the draft-send path needs to POST conversations#create for the first message.
    draft_context: {
      subject,
      mailbox: composeMailbox(originType),
      receiverIds: reps.map((r) => r.slug),
      originType,
      originId,
    },
  };
}

// Build a draft addressed directly to one profile (a PR / promoter), which has no promo-entity
// origin — the recipient is the profile itself, not the promoters who represent an entity.
function buildDraftFromProfile(profile, { subject: subjectOverride } = {}) {
  const recipient = {
    slug: profile.slug,
    name: profile.name,
    profile_image_url: profile.profile_image_url,
    title: profile.current_position_title || '',
    type_of_profile: profile.type_of_profile || 'Id::Profile',
  };
  const subject = (subjectOverride || profile.name || 'New message').slice(0, 200);

  return {
    conversation_id: DRAFT_CONVERSATION_ID,
    is_draft: true,
    subject,
    origin_type: null,
    origin_id: null,
    origin_expert: null,
    origin_product: null,
    conversation_receivers: [recipient],
    conversation_senders: [],
    last_sent_at: null,
    draft_context: {
      subject,
      mailbox: composeMailbox(profile.type_of_profile),
      receiverIds: [recipient.slug],
      originType: null,
      originId: null,
    },
  };
}

// Compose mode starts a conversation, so a viewer who may not start one never enters it.
const readAllowedComposeParams = () => (mayStartConversations() ? readComposeParams() : null);

/**
 * Resolves the inbox "compose mode" URL params into an ephemeral draft conversation.
 *
 * `composeActive` flips synchronously from the URL (before the async resolve finishes) so the inbox
 * can immediately suppress list auto-selection / the empty state and avoid a flash of the wrong pane.
 * `draftConversation` fills in once the recipients are resolved from the entity's representations.
 *
 * @param {number|string} currentReceiverProfileId - the viewer's own profile id
 * @returns {{
 *   composeActive: boolean,
 *   draftConversation: object|null,
 *   isResolvingDraft: boolean,
 *   finishCompose: function,
 * }}
 */
function useComposeDraft(currentReceiverProfileId) {
  const [composeParams, setComposeParams] = useState(readAllowedComposeParams);
  const [draftConversation, setDraftConversation] = useState(null);
  const [isResolvingDraft, setIsResolvingDraft] = useState(Boolean(composeParams));

  // Back/forward can re-enter (or leave) compose mode; keep params in sync with the address bar.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPopState = () => setComposeParams(readAllowedComposeParams());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!composeParams) {
      setDraftConversation(null);
      setIsResolvingDraft(false);
      return undefined;
    }

    let cancelled = false;
    setIsResolvingDraft(true);
    setDraftConversation(null);

    (async () => {
      try {
        if (composeParams.profileId) {
          const { data, error } = await qapi.userProfiles.get(composeParams.profileId);
          if (cancelled) return;
          if (error) {
            qlog.error('Failed to resolve compose recipient:', error);
            setDraftConversation(null);
            return;
          }
          const row = Array.isArray(data) ? data[0] : data;
          setDraftConversation(row ? buildDraftFromProfile(row, composeParams) : null);
          return;
        }

        const { data, error } = await qapi.representations.getAllForPromoted(
          composeParams.originType,
          composeParams.originId,
        );
        if (cancelled) return;
        if (error) {
          qlog.error('Failed to resolve compose recipients:', error);
          setDraftConversation(null);
          return;
        }
        setDraftConversation(buildDraftFromRepresentations(Array.isArray(data) ? data : [], composeParams));
      } catch (err) {
        if (!cancelled) qlog.error('Failed to resolve compose recipients:', err);
      } finally {
        if (!cancelled) setIsResolvingDraft(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [composeParams, currentReceiverProfileId]);

  // Called once the first message has created the real conversation: drop the draft and clear the
  // compose params from the URL so the normal ?conversation=<uuid> reflection can take over.
  const finishCompose = useCallback(() => {
    stripComposeParamsFromUrl();
    setComposeParams(null);
    setDraftConversation(null);
    setIsResolvingDraft(false);
  }, []);

  return {
    composeActive: Boolean(composeParams),
    draftConversation,
    isResolvingDraft,
    finishCompose,
  };
}

export default useComposeDraft;
